import type { LocalDate } from "@js-joda/core";
import cn from "classnames";
import {
	type Dispatch,
	type FC,
	type SetStateAction,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Col, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
	getSocialPlatforms,
	Platform,
	postLabel,
	type SocialPlatform,
	TimePeriod,
	type TopEngagedPostDto,
} from "shared";
import { IconSVG } from "../common/Icon";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { ButtonIcon } from "../components/ButtonIcon";
import { Card } from "../components/Card";
import { SegmentedControl } from "../components/SegmentedControl";
import { StatCard } from "../components/StatCard";
import {
	getCurrentMonthRange,
	getCurrentQuarterRange,
	getCurrentYearRange,
} from "../helpers/time-range";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { useCanEditMetaAds } from "../meta-ads/useMyMetaAdsRole";
import area_chart from "./area_chart.svg";
import bar_chart from "./bar_chart.svg";
import { Chart } from "./Chart";
import comment from "./comment.svg";
import followers from "./followers.svg";
import like from "./like.svg";
import share from "./share.svg";
import { useReport, useTopEngagedPosts } from "./useReport";
import viewsIcon from "./views.svg";

const REPORT_PLATFORM_ICONS: Record<
	SocialPlatform,
	(typeof IconSVG)[keyof typeof IconSVG]
> = {
	[Platform.FACEBOOK]: IconSVG.Facebook,
	[Platform.INSTAGRAM]: IconSVG.Instagram,
	[Platform.THREADS]: IconSVG.BrandThreads,
	[Platform.LINKEDIN]: IconSVG.LinkedIn,
	[Platform.GOOGLE_BUSINESS]: IconSVG.Google,
	[Platform.YOUTUBE]: IconSVG.YouTube,
	[Platform.TIKTOK]: IconSVG.TikTok,
	[Platform.X]: IconSVG.BrandX,
};

const CreateCampaignFromPostButton: FC<{ post: TopEngagedPostDto }> = ({
	post,
}) => {
	const navigate = useNavigate();
	const canEdit = useCanEditMetaAds();
	if (!canEdit) {
		return null;
	}
	return (
		<Button
			size="sm"
			onClick={() =>
				navigate(LinkGen.MetaAds.CampaignCreate(), {
					state: {
						fromPost: {
							subject: post.subject,
							content: post.content,
						},
					},
				})
			}
		>
			Create Campaign
		</Button>
	);
};

const rangeSetter = (
	dates: LocalDate[],
	setStart: Dispatch<SetStateAction<LocalDate>>,
	setEnd: Dispatch<SetStateAction<LocalDate>>,
) => {
	const [start, end] = dates;
	setStart(start);
	setEnd(end);
};

export const PageReport = () => {
	const company = useCompany();
	const monthRange = useMemo(() => getCurrentMonthRange(), []);
	const isTikTokFeatureEnabled = useFeatureFlag("TikTokIntegration");
	const isYouTubeFeatureEnabled = useFeatureFlag("YouTubeIntegration");
	const isXFeatureEnabled = useFeatureFlag("XIntegration");
	const isMetaAdsEnabled = useFeatureFlag("MetaAdsIntegration");

	// Same platform list as post create — show all, connected or not.
	const reportPlatforms = useMemo(() => {
		return getSocialPlatforms().filter((platform) => {
			if (platform === Platform.YOUTUBE && !isYouTubeFeatureEnabled) {
				return false;
			}
			if (platform === Platform.TIKTOK && !isTikTokFeatureEnabled) {
				return false;
			}
			if (platform === Platform.X && !isXFeatureEnabled) {
				return false;
			}
			return true;
		});
	}, [isYouTubeFeatureEnabled, isTikTokFeatureEnabled, isXFeatureEnabled]);

	const {
		platforms,
		totalFollowers,
		likes,
		comments,
		shares,
		views,
		dataLength,
		data,
		followersGrowth,
		followersDiff,
		setFromDate,
		setToDate,
		refetch,
		handlePlatformToggleChange,
		forceRefresh,
	} = useReport(monthRange, reportPlatforms);

	const [period, setPeriod] = useState<TimePeriod>(TimePeriod.MONTH);
	const [topPostsRange, setTopPostsRange] = useState<[LocalDate, LocalDate]>([
		monthRange[0],
		monthRange[1],
	]);

	const selectedPlatforms = useMemo(
		() => Array.from(platforms) as SocialPlatform[],
		[platforms, platforms.size],
	);

	const { data: topPosts, isLoading: topPostsLoading } = useTopEngagedPosts(
		topPostsRange[0],
		topPostsRange[1],
		10,
		selectedPlatforms,
	);

	const handleRangeChange = useCallback(
		(range: LocalDate[], period: TimePeriod) => {
			setPeriod(period);
			rangeSetter(range, setFromDate, setToDate);
			setTopPostsRange([range[0], range[1]]);
		},
		[setFromDate, setToDate],
	);

	// run when period or platforms change to refetch data
	useEffect(() => {
		if (period || platforms.size) {
			refetch();
		}
	}, [refetch, period, platforms, platforms.size]);

	const handleAllToggle = () => {
		for (const platform of reportPlatforms) {
			platforms.add(platform);
		}
	};

	return (
		<AppPage
			title="Reports"
			subtitle="Track audience growth and engagement across your social platforms."
			headerActions={
				company.demo ? (
					<Button
						onClick={() => forceRefresh.mutate()}
						loading={forceRefresh.isPending}
						size="sm"
					>
						Refresh report
					</Button>
				) : undefined
			}
		>
			<Row>
				<Col md={12}>
					<Card className="report-toolbar d-flex align-items-center flex-row justify-content-between">
						<div className="social-container">
							<button
								type="button"
								className="report-all"
								onClick={handleAllToggle}
							>
								All
							</button>
							<div className="social-buttons">
								{reportPlatforms.map((platform) => (
									<div key={platform} className="social-buttons-wrapper">
										<ButtonIcon
											icon={REPORT_PLATFORM_ICONS[platform]}
											onClick={() => handlePlatformToggleChange(platform)}
											className={cn("button-icon", {
												active: platforms.has(platform),
											})}
										/>
									</div>
								))}
							</div>
						</div>

						<SegmentedControl
							ariaLabel="Report time range"
							value={period}
							onChange={(next) => {
								if (next === TimePeriod.MONTH) {
									handleRangeChange(getCurrentMonthRange(), TimePeriod.MONTH);
								} else if (next === TimePeriod.QUARTER) {
									handleRangeChange(
										getCurrentQuarterRange(),
										TimePeriod.QUARTER,
									);
								} else {
									handleRangeChange(getCurrentYearRange(), TimePeriod.YEAR);
								}
							}}
							options={[
								{ value: TimePeriod.MONTH, label: "Month" },
								{ value: TimePeriod.QUARTER, label: "Quarter" },
								{ value: TimePeriod.YEAR, label: "Year" },
							]}
						/>
					</Card>
				</Col>
				<Col md={3} className="mb-4">
					<StatCard
						title="All Followers"
						value={totalFollowers ?? "-"}
						icon={<img src={followers} alt="" />}
					/>
				</Col>
				<Col md={9} className="mb-4">
					<Card className="stat">
						<Card.Header>Engagement</Card.Header>
						<Card.Body>
							<div className="group-activity">
								<div className="group-item">
									<span className="group-title">Views</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={viewsIcon} alt="" />
										</span>
										<span className="group-number">{views}</span>
									</div>
								</div>
								<div className="group-item">
									<span className="group-title">Likes</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={like} alt="" />
										</span>
										<span className="group-number">{likes}</span>
									</div>
								</div>
								<div className="group-item">
									<span className="group-title">Comments</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={comment} alt="" />
										</span>
										<span className="group-number">{comments}</span>
									</div>
								</div>
								<div className="group-item">
									<span className="group-title">Shares</span>
									<div className="group-content">
										<span className="group-metric-icon">
											<img src={share} alt="" />
										</span>
										<span className="group-number">{shares}</span>
									</div>
								</div>
							</div>
						</Card.Body>
					</Card>
				</Col>
				<Col>
					<Card className="my-4">
						<Card.Header>
							Audience Growth
							<div className="chart-icons">
								<img src={area_chart} alt="Area Chart" />
								<img src={bar_chart} alt="Bart Chart" />
							</div>
						</Card.Header>
						<Card.Body>
							{!!dataLength && (
								<>
									Followers gained by{" "}
									<span className="statistics-title">month</span>
									<Chart data={data ?? {}} />
									<Table borderless className="mb-0">
										<thead>
											<tr>
												<th>Audience Growth Metrics</th>
												<th>Totals</th>
												<th>% Change</th>
											</tr>
										</thead>
										<tbody>
											<tr>
												<td className="statistics-title">Total Followers</td>
												<td className="statistics-number">{totalFollowers}</td>
												<td className="statistics-number">
													{followersGrowth.toFixed(2)}%
												</td>
											</tr>
											<tr>
												<td className="statistics-title">
													Total Followers Growth
												</td>
												<td className="statistics-number">{followersDiff}</td>
											</tr>
										</tbody>
									</Table>
								</>
							)}
							{!dataLength && (
								<p className="mb-0 text-muted">
									We are collecting data. Daily sync needs a day or two before
									trends appear — check back soon.
								</p>
							)}
						</Card.Body>
					</Card>
				</Col>
				<Col md={12}>
					<Card className="mb-4">
						<Card.Header>Top 10 Most Engaged Posts</Card.Header>
						<Card.Body>
							{topPostsLoading && <p>Loading top posts...</p>}
							{!topPostsLoading && (!topPosts || topPosts.length === 0) && (
								<p>No published posts found for this period.</p>
							)}
							{!topPostsLoading && topPosts && topPosts.length > 0 && (
								<Table hover responsive>
									<thead>
										<tr>
											<th>#</th>
											<th>Content</th>
											<th>Platform</th>
											<th>Published</th>
											<th>
												<img
													src={viewsIcon}
													alt="Views"
													width={16}
													height={16}
												/>{" "}
												Views
											</th>
											<th>
												<img src={like} alt="Likes" width={16} height={16} />{" "}
												Likes
											</th>
											<th>
												<img
													src={comment}
													alt="Comments"
													width={16}
													height={16}
												/>{" "}
												Comments
											</th>
											<th>
												<img src={share} alt="Shares" width={16} height={16} />{" "}
												Shares
											</th>
											<th>Total</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody>
										{topPosts.map((post: TopEngagedPostDto, index: number) => (
											<tr key={post.id}>
												<td>{index + 1}</td>
												<td
													style={{
														maxWidth: "300px",
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
													title={post.content}
												>
													{postLabel(post)}
													{post.content.length > 50 && "..."}
												</td>
												<td>
													<ButtonIcon
														icon={
															REPORT_PLATFORM_ICONS[
																post.platform as SocialPlatform
															] ?? IconSVG.LinkedIn
														}
														className="button-icon active"
														style={{ cursor: "default" }}
													/>
												</td>
												<td>{post.publishedAt.split("T")[0]}</td>
												<td>{post.views}</td>
												<td>{post.likes}</td>
												<td>{post.comments}</td>
												<td>{post.shares}</td>
												<td>
													<strong>{post.totalEngagement}</strong>
												</td>
												<td>
													{isMetaAdsEnabled && (
														<Suspense fallback={null}>
															<CreateCampaignFromPostButton post={post} />
														</Suspense>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							)}
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</AppPage>
	);
};
